/**
 * @module data/phases
 *
 * The heart of the site: the TOGAF Architecture Development Method (ADM),
 * narrated phase by phase and extended with the AI capabilities that modern
 * transformations depend on.
 *
 * The ADM is a cyclic method. It begins with a **Preliminary Phase** that
 * establishes the architecture capability, then runs phases **A → H** around a
 * loop that is continuously fed by **Requirements Management**.
 *
 * This array is the single source of truth for the visualisation. Copy, colour,
 * ordering and the AI annotations all live here, which means the entire site can
 * be re-authored by editing data alone.
 *
 * @typedef {Object} AiPattern
 * @property {string} title  Short name of the AI pattern.
 * @property {string} body   What it does, in one or two sentences.
 * @property {string} [agent] Named agent/assistant that embodies the pattern.
 *
 * @typedef {Object} PhaseAi
 * @property {string} headline  One-line summary of how AI changes this phase.
 * @property {string} body      Longer explanation.
 * @property {AiPattern[]} patterns  Concrete AI patterns applied in the phase.
 *
 * @typedef {Object} Phase
 * @property {string}   id            Stable slug used for DOM ids and deep links.
 * @property {string}   code          ADM label: `P`, `A` … `H`, or `RM` for the hub.
 * @property {number}   index         0-based position in the cycle.
 * @property {string}   name          Display name.
 * @property {string}   shortName     Compact label for the orbit node and its tooltip.
 * @property {string}   tagline       One-line hook.
 * @property {string}   summary       Overview card paragraph.
 * @property {string}   narrative     Storytelling deep-dive copy.
 * @property {string}   focus         The single question the phase answers.
 * @property {string[]} activities    Key activities performed.
 * @property {string[]} deliverables  Primary artefacts produced.
 * @property {string}   qualityGate   Exit criterion that closes the phase.
 * @property {string[]} capabilities  Ids of capabilities most active here.
 * @property {string}   accent        Primary accent colour (hex).
 * @property {string}   accentSoft    Translucent accent for glows and rings.
 * @property {string}   iconPath      Inline SVG path data on a 24×24 viewBox.
 * @property {PhaseAi}  ai            How AI reshapes the phase.
 */

/** @type {Phase[]} */
export const PHASES = [
  {
    id: "preliminary",
    code: "P",
    index: 0,
    name: "Preliminary",
    shortName: "P · Preliminary",
    tagline: "Build the capability before the architecture.",
    summary:
      "Establish the architecture capability itself: principles, governance, the framework tail and the people who will do the work.",
    narrative:
      "Most transformations fail before they begin, because nobody decided who is allowed to decide. The Preliminary Phase answers that: architecture principles are agreed, a governance model is stood up, the ADM is tailored to the enterprise, and the architecture team is given a mandate rather than a job description. In an AI-era enterprise this means one additional question — where does the model, the data and the agent sit in the organisation's decision rights?",
    focus: "How does this organisation do architecture, and who owns it?",
    activities: [
      "Define architecture principles and their rationale",
      "Stand up the architecture board and decision rights",
      "Tailor the ADM to the enterprise's maturity and cadence",
      "Establish the architecture repository and reference models",
      "Classify the AI, data and model assets the enterprise owns",
    ],
    deliverables: [
      "Architecture principles catalogue",
      "Tailored ADM and architecture governance framework",
      "Architecture repository and capability assessment",
    ],
    qualityGate: "Architecture capability, principles and mandate approved",
    capabilities: ["operating-model", "governance-risk", "talent-enablement"],
    accent: "#9db08a",
    accentSoft: "rgba(157, 176, 138, 0.22)",
    iconPath:
      "M12 2 3 6.5v6c0 5 3.8 8.1 9 9.5 5.2-1.4 9-4.5 9-9.5v-6L12 2Zm0 2.2 6.8 3.4v4.9c0 3.9-2.8 6.4-6.8 7.6-4-1.2-6.8-3.7-6.8-7.6V7.6L12 4.2Z",
    ai: {
      headline: "Decide what the organisation is allowed to delegate.",
      body: "Before a single agent is built, the Preliminary Phase settles the delegation boundary: which decisions may be taken autonomously, which require human confirmation, and which must never leave a human desk. That boundary becomes a first-class architecture principle, not a policy PDF.",
      patterns: [
        {
          title: "Delegation register",
          body: "A living register that classifies every business decision by autonomy level — advisory, co-pilot, autonomous-with-review, autonomous — and names its accountable owner.",
        },
        {
          title: "Architecture principles for AI",
          body: "Reusable principles such as 'no model without a data contract' and 'every automated decision is explainable to its owner' that bind all later phases.",
        },
        {
          title: "Capability baseline",
          body: "An AI-maturity assessment across data, platform, skills and governance that sets a realistic starting point instead of an aspirational one.",
        },
      ],
    },
  },
  {
    id: "vision",
    code: "A",
    index: 1,
    name: "Architecture Vision",
    shortName: "A · Vision",
    tagline: "Agree what the change is for.",
    summary:
      "Frame the transformation: the drivers, the value case, the stakeholders and the architecture vision that everything downstream must serve.",
    narrative:
      "Phase A converts a vague ambition into a statement the whole enterprise can argue with. Business drivers and goals are stated plainly, stakeholders are mapped to their concerns, and a value case sets the bar the architecture must clear. The output is a vision — deliberately at the level of intent, not solution — plus the Statement of Architecture Work that authorises the rest of the cycle.",
    focus: "What outcome are we buying, and who has to agree it is worth it?",
    activities: [
      "Capture business drivers, goals and constraints",
      "Map stakeholders and their concerns",
      "Define the scope, boundaries and value case",
      "Validate AI feasibility and value hypotheses",
      "Produce the Statement of Architecture Work",
    ],
    deliverables: [
      "Architecture vision and value case",
      "Stakeholder map and concerns register",
      "Statement of Architecture Work",
    ],
    qualityGate: "Architecture vision and Statement of Architecture Work approved",
    capabilities: ["business-architecture", "value-portfolio", "governance-risk", "responsible-ai"],
    accent: "#d8a657",
    accentSoft: "rgba(216, 166, 87, 0.22)",
    iconPath:
      "M12 5c-5 0-9 4.6-9 7s4 7 9 7c2.6 0 5-1.2 6.6-3M12 5c3.6 0 6.6 2.3 8 4.5M12 5V3M3 12h4M17 12h4M12 12a2.4 2.4 0 1 1 0-.1M15 19l-2-4",
      ai: {
      headline: "Stress-test the business case before you fund it.",
      body: "A value hypothesis that cannot name its data, its baseline and its decision owner is a wish. In Phase A the AI value case is interrogated against those three tests, and the ambition is sized against what the current data estate can actually support.",
      patterns: [
        {
          title: "Value hypothesis canvas",
          body: "Every candidate AI initiative is expressed as a measurable hypothesis — metric, baseline, target, owner and time horizon — and ranked by value against feasibility.",
        },
        {
          title: "Data readiness pre-flight",
          body: "A rapid assessment of data availability, quality and legal basis for each hypothesis, so that vision-phase optimism is tempered by estate-phase reality.",
        },
        {
          title: "Vision co-pilot",
          body: "An assistant that drafts candidate architecture visions and stakeholder summaries from the workshop transcript, for the architect to challenge and sharpen.",
          agent: "Vision co-pilot",
        },
      ],
    },
  },
  {
    id: "business",
    code: "B",
    index: 2,
    name: "Business Architecture",
    shortName: "B · Business",
    tagline: "Describe the business as a system of capabilities.",
    summary:
      "Model how the enterprise creates value today and how it must change: capabilities, value streams, processes, organisation and information.",
    narrative:
      "Phase B replaces the org chart with a capability map. Value streams show how work actually flows to a customer outcome; processes are attached to those streams; and the organisation structure is assessed for the gap between who it is and who it needs to be. This is where AI stops being a technology question and becomes a work-design question — which steps in a value stream exist only because a human had to do them?",
    focus: "Which capabilities and value streams must the enterprise change?",
    activities: [
      "Develop the business capability model and heat map",
      "Map value streams and their contributing capabilities",
      "Assess the process landscape against the target capabilities",
      "Identify work steps that can be automated, augmented or retired",
      "Define the organisational and role implications",
    ],
    deliverables: [
      "Business capability model with heat map",
      "Value stream and process architecture",
      "Business gap analysis and organisational impact",
    ],
    qualityGate: "Target business architecture agreed by business owners",
    capabilities: ["business-architecture", "process-intelligence", "change-adoption", "measurement-analytics"],
    accent: "#c9825a",
    accentSoft: "rgba(201, 130, 90, 0.22)",
    iconPath:
      "M3 19h18M5 19V9m4 10V5m4 14v-7m4 7V8m4 11V11",
    ai: {
      headline: "Redesign the work, not just the tooling.",
      body: "Capability heat maps are usually drawn from opinion. Phase B mines the systems that actually run the business — process logs, tickets, documents — to locate the friction empirically, then tests each step against a single question: does this still need a human, and if so, which one?",
      patterns: [
        {
          title: "Evidence-based capability heat map",
          body: "Capability scores are derived from process mining, effort data and customer-outcome measures rather than workshop sentiment, and refreshed as the estate changes.",
        },
        {
          title: "Value-stream autonomy analysis",
          body: "Each value-stream step is classified as automate, augment, preserve-human or retire — producing a work-design blueprint rather than a tool wish list.",
        },
        {
          title: "Organisation-change simulation",
          body: "Role and span-of-control implications are modelled against the future capability mix, so the people impact is known before it is announced.",
        },
      ],
    },
  },
  {
    id: "information-systems",
    code: "C",
    index: 3,
    name: "Information Systems",
    shortName: "C · Systems",
    tagline: "Design the data and the applications together.",
    summary:
      "Define the data architecture and the application architecture that will make the business architecture real.",
    narrative:
      "Phase C has two halves that are increasingly one conversation: data and applications. The data architecture defines the entities, ownership, lineage and quality thresholds that the business depends on. The application architecture defines the services, interfaces and rationalisation path. In an AI-native enterprise this phase also introduces a third citizen — the model — with its own lifecycle, provenance and evaluation criteria.",
    focus: "What data and applications must exist to serve the target business?",
    activities: [
      "Model the data architecture: entities, ownership, lineage and quality",
      "Define the application and service architecture",
      "Rationalise the portfolio and plan integration patterns",
      "Design the feature, model and prompt asset lifecycle",
      "Specify retrieval, context and tool boundaries for AI services",
    ],
    deliverables: [
      "Data architecture and data contract catalogue",
      "Application and integration architecture",
      "AI service blueprint: features, models, retrieval and tools",
    ],
    qualityGate: "Data and application architectures reviewed and baselined",
    capabilities: ["data-ai-architecture", "application-integration", "security-risk", "responsible-ai"],
    accent: "#6f9fbe",
    accentSoft: "rgba(111, 159, 190, 0.22)",
    iconPath:
      "M4 5h16M4 12h16M4 19h16M8 3v4M16 10v4M10 17v4",
    ai: {
      headline: "Treat data contracts and model contracts as architecture.",
      body: "Phase C is where AI programmes are won or lost. Retrieval quality depends on entity ownership; agent reliability depends on interface contracts; and both depend on lineage you can prove. The architecture therefore specifies not just what the system does, but what it is allowed to know.",
      patterns: [
        {
          title: "Data contracts as architecture artefacts",
          body: "Every domain publishes a versioned contract — schema, semantics, quality SLA, legal basis and owner — that downstream models and agents consume instead of scraping tables.",
        },
        {
          title: "Model and prompt registry",
          body: "A governed registry for models, prompts, evaluation sets and versions, with the same change discipline applied to application code.",
        },
        {
          title: "Retrieval boundary design",
          body: "Explicit design of what an AI service may retrieve, from which store, under whose entitlement — decided at architecture time rather than patched at incident time.",
        },
      ],
    },
  },
  {
    id: "technology",
    code: "D",
    index: 4,
    name: "Technology Architecture",
    shortName: "D · Technology",
    tagline: "Give the architecture a place to run.",
    summary:
      "Define the platforms, infrastructure and standards that host the target architecture, including the AI runtime and its cost model.",
    narrative:
      "Phase D turns the logical design into a deployable one: compute, network, storage, platforms and the standards that keep them coherent. For AI workloads this phase carries unusual weight. Inference is a running cost, not a licence, and the architecture must decide where inference happens — frontier API, private endpoint or on-device — with the cost, latency, residency and confidentiality consequences of that choice made explicit.",
    focus: "On what platforms, at what cost, and under which constraints?",
    activities: [
      "Define the technology platform and infrastructure services",
      "Specify the AI runtime: inference, orchestration, vector and feature stores",
      "Set the cost, latency and residency envelope for each workload",
      "Establish technology standards and the non-negotiable constraints",
      "Plan the observability, evaluation and telemetry stack",
    ],
    deliverables: [
      "Technology architecture and standards catalogue",
      "AI platform and runtime design",
      "Cost, latency and residency envelope per workload",
    ],
    qualityGate: "Technology architecture approved and platform standards published",
    capabilities: ["data-ai-architecture", "application-integration", "security-risk"],
    accent: "#8292ad",
    accentSoft: "rgba(130, 146, 173, 0.22)",
    iconPath:
      "M4 5h16v5H4V5Zm0 9h7v5H4v-5Zm11 0h5v5h-5v-5Z",
    ai: {
      headline: "Inference is an operating cost — design it like one.",
      body: "A model call has a price, a latency and a jurisdiction. Phase D makes those three visible per workload, so the architecture can route cheap work to small models, sensitive work to private endpoints, and interactive work to whatever is fast enough.",
      patterns: [
        {
          title: "Model routing tiers",
          body: "Declared tiers — small local model, private hosted model, frontier API — with routing rules based on task complexity, data classification and latency budget.",
        },
        {
          title: "Cost and carbon envelope",
          body: "Every AI service carries a per-transaction cost and energy budget, monitored in production like any other SLO.",
        },
        {
          title: "Evaluation harness by default",
          body: "Golden datasets, regression suites and human-review sampling are provisioned as platform capabilities, not rebuilt per project.",
        },
      ],
    },
  },
  {
    id: "opportunities",
    code: "E",
    index: 5,
    name: "Opportunities & Solutions",
    shortName: "E · Solutions",
    tagline: "Decide the work packages — and the build-or-buy call.",
    summary:
      "Consolidate the gaps into work packages, define transition architectures and choose between building, buying and configuring.",
    narrative:
      "Phase E is the pivot from design to delivery. The gaps identified in B, C and D are grouped into coherent work packages, each with a business value, a dependency profile and a delivery route. For AI components the make-or-buy question has a fourth option that rarely appears on a procurement form: configure an existing model with a governed context layer, and treat the capability as assembled rather than built.",
    focus: "Which coherent work packages should we actually commit to?",
    activities: [
      "Consolidate gaps into work packages with owners",
      "Define transition architectures and their sequencing",
      "Evaluate build, buy, configure or assemble for each component",
      "Assess delivery risk, dependency and value per package",
      "Confirm the delivery routes with delivery partners and vendors",
    ],
    deliverables: [
      "Work package definitions with value and risk profiles",
      "Transition architecture roadmap",
      "Build/buy/configure/assemble decisions",
    ],
    qualityGate: "Work packages, transition architectures and sourcing decisions agreed",
    capabilities: ["value-portfolio", "application-integration", "operating-model"],
    accent: "#a98bc4",
    accentSoft: "rgba(169, 139, 196, 0.22)",
    iconPath:
      "M12 2 2 7l10 5 5-2.5V17h2V9.5L12 2Zm-6 11v3.5c0 1.9 2.7 3.5 6 3.5s6-1.6 6-3.5V13l-6 3-6-3Z",
    ai: {
      headline: "Prefer the assembled capability over the bespoke build.",
      body: "The fastest path to value is usually a configured model behind a governed context layer, wrapped in your own process and evaluation. Phase E makes that the default hypothesis and requires an argument to depart from it.",
      patterns: [
        {
          title: "Assemble-first sourcing",
          body: "A fourth sourcing route — assemble — sits beside build, buy and configure, and covers frontier models plus your own retrieval, guardrails and evaluation.",
        },
        {
          title: "Work package value/risk matrix",
          body: "Packages are plotted by value and delivery risk, producing a sequence that buys credibility early and defers the speculative work.",
        },
        {
          title: "Transition architecture rehearsal",
          body: "Each transition is walked through as a narrative — who does what, with which system, in which week — to expose sequencing errors before funding.",
        },
      ],
    },
  },
  {
    id: "migration",
    code: "F",
    index: 6,
    name: "Migration Planning",
    shortName: "F · Migration",
    tagline: "Sequence the change so the business can absorb it.",
    summary:
      "Turn the roadmap into a costed, resourced, dependency-aware plan with explicit business-value milestones.",
    narrative:
      "Phase F is where good architecture meets real calendars, real budgets and real people who still have a day job. Work packages become a phased implementation and migration plan, with dependencies, resource profiles, cost and the absorption capacity of the business taken into account. Change fatigue is a planning constraint, not an excuse.",
    focus: "In what order, at what cost, and at what rate of change?",
    activities: [
      "Build the phased implementation and migration plan",
      "Cost the plan and model the resourcing profile",
      "Sequence dependencies and value milestones",
      "Assess business absorption capacity and change load",
      "Define the benefit-realisation and tracking baseline",
    ],
    deliverables: [
      "Implementation and migration plan",
      "Cost, resource and change-load profile",
      "Benefit realisation baseline",
    ],
    qualityGate: "Migration plan funded, resourced and baseline agreed",
    capabilities: ["value-portfolio", "change-adoption", "talent-enablement", "measurement-analytics"],
    accent: "#5fd4c4",
    accentSoft: "rgba(95, 212, 196, 0.22)",
    iconPath:
      "M4 6h6v3H4V6Zm10 9h6v3h-6v-3ZM7 9v6h3M14 15h-4v.5",
    ai: {
      headline: "Plan the change the organisation can absorb, not the one the roadmap wants.",
      body: "Change load is modelled explicitly: how many process, role and system changes each team faces in each quarter. Where the curve spikes, the plan is re-sequenced — because an unadopted capability has zero value however well it was built.",
      patterns: [
        {
          title: "Change-load model",
          body: "A per-team, per-quarter view of simultaneous change, used as a hard constraint when sequencing work packages.",
        },
        {
          title: "Adoption leading indicators",
          body: "Usage, override and quality-signal metrics defined in advance, so benefit tracking starts on day one rather than at the first review.",
        },
        {
          title: "Plan simulation",
          body: "Dependency and capacity scenarios are simulated to expose the two or three decisions that actually determine the schedule.",
        },
      ],
    },
  },
  {
    id: "implementation-governance",
    code: "G",
    index: 7,
    name: "Implementation Governance",
    shortName: "G · Governance",
    tagline: "Hold the architecture while the project runs.",
    summary:
      "Govern delivery against the architecture: compliance reviews, dispensations, contracts and architecture change control.",
    narrative:
      "Phase G is the phase most often skipped and most often missed. It is where the architecture board actually meets delivery — reviewing design against the target, granting or refusing dispensations, and enforcing the Non-Functional Requirements that keep the whole thing coherent. Without it, the target architecture becomes a document that describes a system nobody built.",
    focus: "Is what we are building still what we said we would build?",
    activities: [
      "Run architecture compliance reviews at each delivery gate",
      "Manage dispensation requests and their remediation",
      "Govern supplier and partner architecture obligations",
      "Monitor non-functional requirements and quality attributes",
      "Operate architecture change control on the target baseline",
    ],
    deliverables: [
      "Architecture contracts and compliance records",
      "Dispensation register with remediation plans",
      "Non-functional requirement compliance reports",
    ],
    qualityGate: "Deployed solution compliant, or dispensation formally accepted",
    capabilities: ["governance-risk", "security-risk", "operating-model", "responsible-ai"],
    accent: "#e0b584",
    accentSoft: "rgba(224, 181, 132, 0.22)",
    iconPath:
      "M12 2 4 6v6c0 4.6 3.3 7.7 8 9 4.7-1.3 8-4.4 8-9V6l-8-4Zm-1 12-3-3 1.4-1.4L11 11.2l4.6-4.6L17 8l-6 6Z",
    ai: {
      headline: "Govern the model the way you govern the code.",
      body: "A change to a prompt, a retrieval index or a model version is a change to the system's behaviour. Phase G extends the compliance review to cover those artefacts, and requires evidence — evaluation results, drift signals, human-review samples — rather than assurances.",
      patterns: [
        {
          title: "AI change control",
          body: "Model, prompt and index changes pass through the same review, evidence and rollback discipline as application releases.",
        },
        {
          title: "Automated compliance evidence",
          body: "Architecture rules are encoded as checks that run in the delivery pipeline, so compliance is measured continuously rather than asserted at a gate.",
        },
        {
          title: "Behavioural regression gates",
          body: "Every release must clear a behavioural regression suite — accuracy, refusal, fairness and cost — before it can progress.",
        },
      ],
    },
  },
  {
    id: "change-management",
    code: "H",
    index: 8,
    name: "Architecture Change Management",
    shortName: "H · Change",
    tagline: "Keep the architecture alive, or watch it become history.",
    summary:
      "Monitor the deployed architecture for change drivers and decide whether to maintain, incrementally change or re-architect.",
    narrative:
      "Phase H closes the loop and reopens it. The enterprise is watched for the drivers that invalidate assumptions — new regulation, new model capability, a competitor, a cost shift. The architecture board then makes a deliberate choice: maintain, migrate incrementally, or re-architect. The cycle returns to Phase A, better informed than the last time. This is where method becomes habit.",
    focus: "What has changed, and does our architecture still hold?",
    activities: [
      "Monitor technology, regulatory and market change drivers",
      "Assess the impact of change on the architecture baseline",
      "Classify responses: maintain, incremental change or re-architecture",
      "Feed findings and lessons into the next ADM cycle",
      "Track realised value against the original business case",
    ],
    deliverables: [
      "Architecture change impact assessment",
      "Change classification and response decision",
      "Value realisation report and cycle retrospective",
    ],
    qualityGate: "Change decision recorded and next cycle initiated",
    capabilities: ["governance-risk", "value-portfolio", "data-ai-architecture"],
    accent: "#c4a35a",
    accentSoft: "rgba(196, 163, 90, 0.22)",
    iconPath:
      "M12 4V1.5L8 5.5l4 4V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-8Z",
    ai: {
      headline: "Let the architecture notice change before the market does.",
      body: "Phase H is the enterprise's peripheral vision. Signals are gathered deliberately — model release notes, regulatory dockets, cost curves, incident patterns, adoption telemetry — and turned into a standing agenda item for the architecture board rather than a surprise.",
      patterns: [
        {
          title: "Capability drift detection",
          body: "Production telemetry — accuracy, override rates, cost per transaction, escalation patterns — is monitored against the baseline to detect silent capability decay.",
        },
        {
          title: "Change-radar briefing",
          body: "A quarterly synthesis of model, platform, regulatory and market movement, each item pre-assessed for impact on the architecture baseline.",
        },
        {
          title: "Value reconciliation",
          body: "Realised benefit is compared against the Phase A value case, and the variance is used to recalibrate the next cycle's hypotheses.",
        },
      ],
    },
  },
];

/**
 * The **Requirements Management** hub that sits at the centre of the ADM cycle.
 *
 * It is not a phase with a start and an end — it is a continuous activity that
 * every phase feeds and draws from.
 *
 * @typedef {Object} RequirementsHub
 * @property {string} id
 * @property {string} code
 * @property {string} name
 * @property {string} tagline
 * @property {string} narrative
 * @property {string[]} activities
 * @property {string} accent
 */

/** @type {RequirementsHub} */
export const REQUIREMENTS_HUB = {
  id: "requirements",
  code: "RM",
  name: "Requirements Management",
  tagline: "The centre that never stops turning.",
  narrative:
    "Requirements Management runs through every phase. Business requirements are captured, stored, prioritised and fed forward; architecture requirements flow back. Nothing in the ADM is decided without a traceable requirement behind it — and with AI in the estate, that traceability is what makes automated decisions defensible.",
  activities: [
    "Capture and structure business and architecture requirements",
    "Maintain traceability from requirement to capability to decision",
    "Prioritise against value, risk and regulatory obligation",
    "Propagate approved change through the requirement baseline",
  ],
  accent: "#5fd4c4",
};

/**
 * The four architecture domains shown in the "domains" band. These are the
 * lenses the ADM applies repeatedly.
 *
 * @typedef {Object} Domain
 * @property {string} id
 * @property {string} code
 * @property {string} name
 * @property {string} body
 * @property {string} phaseId  The ADM phase that primarily develops it.
 * @property {string} glyph
 */

/** @type {Domain[]} */
export const DOMAINS = [
  {
    id: "business",
    code: "B",
    name: "Business",
    body: "Capabilities, value streams, processes, organisation and the information the business reasons about.",
    phaseId: "business",
    glyph: "layers",
  },
  {
    id: "data",
    code: "C₁",
    name: "Data",
    body: "Entities, ownership, lineage, quality thresholds and the contracts that make data safe to reuse.",
    phaseId: "information-systems",
    glyph: "database",
  },
  {
    id: "application",
    code: "C₂",
    name: "Application",
    body: "Services, interfaces, portfolio rationalisation and the integration patterns that bind them.",
    phaseId: "information-systems",
    glyph: "plug",
  },
  {
    id: "technology",
    code: "D",
    name: "Technology",
    body: "Platforms, runtime, standards, and the cost, latency and residency envelopes that constrain them.",
    phaseId: "technology",
    glyph: "server",
  },
];

/** Phase ids in cycle order. */
export const PHASE_ORDER = PHASES.map((phase) => phase.id);

/**
 * Find a phase by id.
 *
 * @param {string} id
 * @returns {Phase|undefined}
 */
export function getPhaseById(id) {
  return PHASES.find((phase) => phase.id === id);
}

/**
 * Return the phase that follows the given one, wrapping around the cycle.
 *
 * @param {string} id
 * @param {number} [offset=1] Steps to move (may be negative).
 * @returns {Phase}
 */
export function getNeighbouringPhase(id, offset = 1) {
  const current = PHASES.findIndex((phase) => phase.id === id);
  const start = current === -1 ? 0 : current;
  const next = (start + offset + PHASES.length) % PHASES.length;
  return PHASES[next];
}

/**
 * Aggregate every AI pattern defined across all phases.
 *
 * Useful for the search index and for the "AI layer" section, which presents the
 * patterns as one connected system rather than nine separate lists.
 *
 * @returns {Array<AiPattern & { phaseId: string, phaseName: string, phaseCode: string, accent: string }>}
 */
export function getAllAiPatterns() {
  return PHASES.flatMap((phase) =>
    phase.ai.patterns.map((pattern) => ({
      ...pattern,
      phaseId: phase.id,
      phaseName: phase.name,
      phaseCode: phase.code,
      accent: phase.accent,
    }))
  );
}
